import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SidebarTrigger, useSidebar } from '@/components/ui/sidebar';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  FileText, Search, Share2, Users, Activity, BrainCircuit, TrendingUp, AlertTriangle, Settings2, FileDown, Link as LinkIcon, BookOpen, CalendarDays, Target, Filter, ChevronDown, ChevronUp, Trophy, Star, CalendarCheck2, Lightbulb, PlusCircle, Clock
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, ScatterChart, Scatter, ZAxis, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
} from 'recharts';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DateRangePicker } from '@/components/date-range-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import type { DateRange } from 'react-day-picker';
import SEO from '@/components/SEO';

const getUserName = () => "Khafre";
const getUserLearningStreak = () => 12;

const skillProgressData = [
  { name: 'React', progress: 80, goal: 90, priority: true },
  { name: 'Firebase', progress: 70, goal: 85, priority: false },
  { name: 'Next.js', progress: 60, goal: 75, priority: false },
  { name: 'Genkit', progress: 90, goal: 95, priority: true },
  { name: 'Tailwind', progress: 75, goal: 80, priority: false },
  { name: 'TypeScript', progress: 85, goal: 90, priority: false },
];

const userGoals = [
    { id: 'g1', name: 'Master React Hooks', progress: 75, skills: ['React'] },
    { id: 'g2', name: 'Build Fullstack App with Firebase', progress: 50, skills: ['React', 'Firebase', 'Genkit'] },
    { id: 'g3', name: 'Deploy Next.js Site', progress: 30, skills: ['Next.js', 'React'] },
];

const aiRecommendations = [
  { id: 'r1', title: "Advanced Genkit Techniques", type: "Course", estTime: "3 hours", difficulty: "Advanced", reason: "Builds on your current Genkit knowledge.", url: "#" },
  { id: 'r2', title: "Firebase Security Rules Best Practices", type: "Docs", estTime: "1 hour", difficulty: "Intermediate", reason: "Crucial for your Firebase goal.", url: "#" },
  { id: 'r3', title: "State Management Patterns in React", type: "Blog", estTime: "30 mins", difficulty: "Intermediate", reason: "Relevant to your React goal.", url: "#" },
];

const skillGaps = [
  { skill: "Advanced TypeScript", relevance: "High", suggestion: "Complete TypeScript Deep Dive course", url: "#" },
  { skill: "Unit Testing Strategies", relevance: "Medium", suggestion: "Review Jest documentation & examples", url: "#" },
  { skill: "CI/CD Pipeline Optimization", relevance: "Low", suggestion: "Explore GitHub Actions workflows", url: "#" },
];

const averageWeeklyHours = 15;
const currentWeeklyHours = 18;

const learningTrendsData = [
  { name: 'Jan', hours: 10, resources: 5 },
  { name: 'Feb', hours: 15, resources: 8 },
  { name: 'Mar', hours: 12, resources: 6 },
  { name: 'Apr', hours: 18, resources: 10 },
  { name: 'May', hours: 20, resources: 12 },
  { name: 'Jun', hours: 25, resources: 15 },
];

const overallTrend = learningTrendsData[learningTrendsData.length - 1].hours > learningTrendsData[0].hours ? 'Upward' : 'Downward';

const timeAllocationData = [
  { name: 'Courses', value: 45 },
  { name: 'Documentation', value: 25 },
  { name: 'Blogs/Articles', value: 15 },
  { name: 'Video Tutorials', value: 10 },
  { name: 'Guides/HowTos', value: 5 },
];

const preferredLearningStyle = "Courses";

const learningEfficiencyData = [
  { time: 5, outcome: 60, type: 'Blog', title: 'Quick Tip: Tailwind Grid' },
  { time: 10, outcome: 75, type: 'Video', title: 'Firebase Auth Basics' },
  { time: 20, outcome: 85, type: 'Course', title: 'Advanced React Hooks' },
  { time: 8, outcome: 70, type: 'Docs', title: 'Genkit Flow API' },
  { time: 15, outcome: 80, type: 'Guide', title: 'Next.js Routing Guide' },
  { time: 25, outcome: 90, type: 'Course', title: 'TypeScript Fundamentals' },
  { time: 3, outcome: 55, type: 'Blog', title: 'Understanding Closures' },
  { time: 12, outcome: 78, type: 'Docs', title: 'Firestore Security Rules' },
];

const mostUsedResources = [
  { title: "Advanced React Hooks", type: "Course", completion: 85, lastAccessed: "2 days ago", url: "#" },
  { title: "Firestore Security Rules", type: "Docs", completion: 95, lastAccessed: "1 day ago", url: "#" },
  { title: "Genkit Flow API", type: "Docs", completion: 70, lastAccessed: "5 days ago", url: "#" },
  { title: "Next.js Routing Guide", type: "Guide", completion: 80, lastAccessed: "3 days ago", url: "#" },
];

const COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
  'hsl(var(--chart-3))',
];

const HieroglyphChartIcon = () => (
 <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6 text-accent">
    <path d="M12 10a4 4 0 1 0 0 -8a4 4 0 0 0 0 8z" />
    <path d="M12 12v10" />
    <path d="M8 22h8" />
  </svg>
);

export default function Analytics() {
  const { isMobile } = useSidebar();
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [contentType, setContentType] = useState<string>('all');
  const [selectedGoal, setSelectedGoal] = useState<string>('all');
  const [filtersVisible, setFiltersVisible] = useState(false);

  const userName = getUserName();
  const learningStreak = getUserLearningStreak();
  const weeklyHoursComparison = Math.round(((currentWeeklyHours - averageWeeklyHours) / averageWeeklyHours) * 100);

  const filteredEfficiencyData = selectedGoal === 'all'
    ? learningEfficiencyData
    : learningEfficiencyData.filter(item => {
        const goalSkills = userGoals.find(g => g.id === selectedGoal)?.skills || [];
        return goalSkills.some(skill => item.title.toLowerCase().includes(skill.toLowerCase()));
      });

  return (
    <div className="flex flex-col flex-1 overflow-y-auto p-4 md:p-6 lg:p-8 bg-background text-foreground space-y-6">
      <SEO title="Analytics - Ankh Studio" description="Personal Learning Analytics Dashboard" />
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 border-b border-border pb-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-primary flex items-center gap-2">
              <HieroglyphChartIcon />
              Personal Learning Analytics
            </h1>
            <p className="text-muted-foreground mt-1">
              Good morning, {userName}! Currently on a <span className="font-semibold text-accent">{learningStreak}-day</span> learning streak. Keep it up!
            </p>
          </div>
          <div className="flex items-center gap-2 mt-4 md:mt-0">
            <Button variant="outline" size="icon" aria-label="Dashboard Settings">
              <Settings2 className="h-4 w-4" />
              <span className="sr-only">Dashboard Settings</span>
            </Button>
            {isMobile && <SidebarTrigger />}
          </div>
        </div>

        <Accordion type="single" collapsible className="mb-6 bg-card shadow-sm border border-border/50 rounded-lg" value={filtersVisible ? "filters" : ""}>
          <AccordionItem value="filters" className="border-none">
            <AccordionTrigger
              onClick={() => setFiltersVisible(!filtersVisible)}
              className="px-4 py-2 text-sm font-medium text-secondary-foreground hover:bg-muted/50 data-[state=open]:border-b data-[state=open]:border-border/50"
            >
              <div className="flex items-center gap-2">
                 <Filter className="h-4 w-4 text-accent" />
                 <span>Filter Dashboard</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="p-4">
                <div className="flex flex-wrap gap-4 items-center">
                  <div className="flex-grow md:flex-grow-0">
                     <DateRangePicker date={dateRange} onDateChange={setDateRange} />
                  </div>
                  <Select value={selectedGoal} onValueChange={setSelectedGoal}>
                    <SelectTrigger className="w-full md:w-auto min-w-[180px] bg-background border-input">
                      <SelectValue placeholder="Filter by Goal" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Goals</SelectItem>
                      {userGoals.map(goal => (
                         <SelectItem key={goal.id} value={goal.id}>{goal.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={contentType} onValueChange={setContentType}>
                    <SelectTrigger className="w-full md:w-auto min-w-[170px] bg-background border-input">
                      <SelectValue placeholder="Content Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Content Types</SelectItem>
                      <SelectItem value="course">Courses</SelectItem>
                      <SelectItem value="docs">Documentation</SelectItem>
                      <SelectItem value="blog">Blogs/Articles</SelectItem>
                      <SelectItem value="video">Video Tutorials</SelectItem>
                      <SelectItem value="guide">Guides/HowTos</SelectItem>
                    </SelectContent>
                  </Select>
               </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Card className="bg-card shadow-md border-border rounded-lg hover:shadow-lg transition-shadow duration-200">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-primary flex items-center gap-2">
                <Target className="h-5 w-5 text-accent" /> Active Learning Goals
              </CardTitle>
              <CardDescription>Your current objectives and progress.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {userGoals.length > 0 ? userGoals.map(goal => (
                <div key={goal.id} className="space-y-1">
                  <div className="flex justify-between items-center text-sm">
                    <span className="font-medium text-secondary-foreground">{goal.name}</span>
                    <span className="text-xs text-muted-foreground">{goal.progress}%</span>
                  </div>
                  <Progress value={goal.progress} className="h-2" aria-label={`${goal.name} progress ${goal.progress}%`} />
                </div>
              )) : (
                <p className="text-sm text-muted-foreground italic text-center py-4">No active goals set. Define your learning path!</p>
              )}
              <div className="text-center mt-4">
                <Button variant="outline" size="sm">
                    <Target className="mr-2 h-4 w-4 text-accent" /> Manage Goals
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card shadow-md border-border rounded-lg hover:shadow-lg transition-shadow duration-200 relative overflow-hidden">
             <div className="absolute inset-0 opacity-[0.03] bg-[url('/papyrus-light.png')] dark:bg-[url('/papyrus-dark.png')] bg-repeat pointer-events-none"></div>
             <CardHeader className="relative z-10">
              <CardTitle className="text-xl font-semibold text-primary flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-accent" /> Skill Snapshot & Trends
              </CardTitle>
              <CardDescription>Your development vs. goals, and recent progress.</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 relative z-10">
              <div>
                <ResponsiveContainer width="100%" height={200}>
                  <RadarChart cx="50%" cy="50%" outerRadius="80%" data={skillProgressData}>
                    <defs>
                      <linearGradient id="userProgressGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0.3}/>
                      </linearGradient>
                       <linearGradient id="goalLineGradient" x1="0" y1="0" x2="1" y2="0">
                         <stop offset="0%" stopColor="hsl(var(--chart-3))" stopOpacity={0.8}/>
                         <stop offset="100%" stopColor="hsl(var(--chart-3))" stopOpacity={0.4}/>
                       </linearGradient>
                    </defs>
                    <PolarGrid stroke="hsl(var(--border)/50)" />
                    <PolarAngleAxis dataKey="name" tick={({ payload, x, y, textAnchor, index }) => (
                         <text x={x} y={y} textAnchor={textAnchor} fill={skillProgressData[index].priority ? 'hsl(var(--accent))' : 'hsl(var(--muted-foreground))'} fontSize={10} fontWeight={skillProgressData[index].priority ? 600 : 400}>
                            {payload.value}
                         </text>
                     )} />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} />
                    <Radar name="Current Progress" dataKey="progress" stroke="hsl(var(--chart-1))" fill="url(#userProgressGradient)" fillOpacity={0.6} strokeWidth={2}/>
                     <Radar name="Target Goal" dataKey="goal" stroke="url(#goalLineGradient)" fill="none" strokeDasharray="4 4" strokeWidth={1.5} dot={(props) => {
                        const { cx, cy, payload } = props;
                         if (payload.goal) {
                             return <circle cx={cx} cy={cy} r={3} fill="hsl(var(--chart-3))" stroke="hsl(var(--background))" strokeWidth={1} />;
                         }
                         return <></>;
                     }}/>
                    <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--popover))', borderColor: 'hsl(var(--border))' }} />
                     <Legend wrapperStyle={{ fontSize: '10px', paddingTop: '10px' }} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-between items-center mt-2 px-1">
                 <div className="flex items-center gap-1 text-xs text-muted-foreground">
                     <TrendingUp className={`h-4 w-4 ${overallTrend === 'Upward' ? 'text-green-500' : 'text-red-500'}`} />
                     <span>Skill Growth: <span className={`font-semibold ${overallTrend === 'Upward' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{overallTrend}</span></span>
                 </div>
                  <div className="flex items-center gap-1">
                     <Trophy className="h-4 w-4 text-yellow-500" aria-label="Top Learner Badge (Placeholder)" />
                     <Star className="h-4 w-4 text-blue-500" aria-label="React Master Badge (Placeholder)" />
                  </div>
               </div>
            </CardContent>
          </Card>

          <Card className="bg-card shadow-md border-border rounded-lg hover:shadow-lg transition-shadow duration-200">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-primary flex items-center gap-2">
                <BrainCircuit className="h-5 w-5 text-accent" /> AI Learning Guide
              </CardTitle>
              <CardDescription>Actionable steps to accelerate your growth.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="text-sm font-medium mb-2 text-secondary-foreground">Top Recommendations</h3>
                 <ul className="list-none space-y-2 text-sm">
                  {aiRecommendations.slice(0, 2).map(rec => (
                    <li key={rec.id} className="p-2 border border-border/30 rounded-md bg-muted/20 hover:bg-muted/40 transition-colors">
                      <Link to={rec.url} className="flex items-start justify-between gap-2 group">
                        <div>
                          <p className="font-medium group-hover:text-primary">{rec.title} <span className="text-xs text-muted-foreground">({rec.type})</span></p>
                          <p className="text-xs text-muted-foreground mt-0.5">{rec.reason}</p>
                           <div className="flex items-center gap-2 mt-1">
                               <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary">{rec.difficulty}</span>
                               <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><Clock className="h-2.5 w-2.5"/> {rec.estTime}</span>
                           </div>
                        </div>
                        <BookOpen className="h-4 w-4 text-muted-foreground flex-shrink-0 group-hover:text-accent mt-1" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-sm font-medium mb-1 text-secondary-foreground">Focus Areas (Skill Gaps)</h3>
                <ul className="list-none space-y-2 text-sm">
                  {skillGaps.slice(0, 1).map(gap => (
                     <li key={gap.skill} className="flex items-center justify-between">
                      <span className="text-muted-foreground">{gap.skill}</span>
                      <Button variant="link" size="sm" className="p-0 h-auto text-xs text-accent ml-2" asChild>
                        <Link to={gap.url}>{gap.suggestion}</Link>
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-sm font-medium mb-1 text-secondary-foreground">Usage Anomaly</h3>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                   <AlertTriangle className="h-4 w-4 text-yellow-500" /> No significant anomalies detected recently.
                </p>
              </div>

              <div className="text-center mt-4">
                 <Button variant="outline" size="sm" asChild>
                    <Link to="/ai-insights">
                        <BrainCircuit className="mr-2 h-4 w-4 text-accent" /> View Full AI Report
                    </Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card shadow-md border-border rounded-lg hover:shadow-lg transition-shadow duration-200">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-primary flex items-center gap-2">
                <Activity className="h-5 w-5 text-accent" /> Resource Engagement
              </CardTitle>
              <CardDescription>How you're utilizing learning materials.</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground text-center">
                  You spent <span className="font-semibold text-primary">{currentWeeklyHours} hours</span> learning this week, which is{' '}
                  <span className={`font-semibold ${weeklyHoursComparison >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {weeklyHoursComparison >= 0 ? `+${weeklyHoursComparison}` : weeklyHoursComparison}%
                  </span>{' '}
                  {weeklyHoursComparison >= 0 ? 'above' : 'below'} your average.
                </p>
                <ResponsiveContainer width="100%" height={150}>
                  <PieChart>
                    <Pie
                      data={timeAllocationData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={60}
                      innerRadius={30}
                      fill="#8884d8"
                      dataKey="value"
                      paddingAngle={2}
                      label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                      isAnimationActive={false}
                    >
                      {timeAllocationData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="hsl(var(--background))" strokeWidth={2} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--popover))', borderColor: 'hsl(var(--border))' }} />
                    <Legend layout="vertical" align="right" verticalAlign="middle" wrapperStyle={{ fontSize: '10px', lineHeight: '14px' }} iconSize={8} />
                  </PieChart>
                </ResponsiveContainer>
                 <p className="text-xs text-muted-foreground text-center italic">
                    You seem to learn best from <span className="font-semibold text-primary">{preferredLearningStyle}</span>. Consider focusing on it for your next skill.
                 </p>
              </div>
              <div className="overflow-x-auto">
                <h3 className="text-sm font-medium mb-2 text-secondary-foreground">Most Frequented</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="p-2 text-xs h-8">Resource</TableHead>
                      <TableHead className="p-2 text-xs h-8 text-right">Completion</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mostUsedResources.slice(0, 4).map((resource) => (
                      <TableRow key={resource.title} className="hover:bg-muted/30">
                        <TableCell className="font-medium p-2 max-w-[100px] text-xs">
                          <Link to={resource.url} className="hover:text-primary line-clamp-1" title={resource.title}>{resource.title}</Link>
                          <span className="block text-[10px] text-muted-foreground">{resource.type}</span>
                        </TableCell>
                        <TableCell className="p-2 text-right align-middle">
                          <Progress value={resource.completion} className="h-1.5 w-[40px] inline-block" aria-label={`Completion ${resource.completion}%`} />
                          <span className="ml-1.5 text-[10px] text-muted-foreground">{resource.completion}%</span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <p className="text-[10px] text-muted-foreground text-center mt-2">Resources frequently align with active goals.</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card shadow-md border-border rounded-lg hover:shadow-lg transition-shadow duration-200">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-primary flex items-center gap-2">
               <Trophy className="h-5 w-5 text-accent" /> Learning Efficiency
              </CardTitle>
              <CardDescription>Time investment vs. outcome, filtered by goal.</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: -10 }}>
                  <CartesianGrid stroke="hsl(var(--border)/50)" strokeDasharray="3 3"/>
                  <XAxis type="number" dataKey="time" name="Time (hrs)" unit="h" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} label={{ value: 'Time Invested (Hours)', position: 'insideBottom', offset: -15, fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} />
                  <YAxis type="number" dataKey="outcome" name="Outcome Score" unit="%" domain={[0, 100]} tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} label={{ value: 'Outcome/Completion (%)', angle: -90, position: 'insideLeft', fill: 'hsl(var(--muted-foreground))', fontSize: 10, dx: -5 }} />
                  <ZAxis type="category" dataKey="type" name="Resource Type" range={[60, 120]}/>
                  <Tooltip cursor={{ strokeDasharray: '3 3' }} contentStyle={{ backgroundColor: 'hsl(var(--popover))', borderColor: 'hsl(var(--border))' }} itemStyle={{fontSize: '10px'}}
                     formatter={(value, name, props) => [`${props.payload.title} (${value}%)`, 'Outcome']}
                  />
                  <Scatter name="Resource Efficiency" data={filteredEfficiencyData} fill="hsl(var(--chart-2))">
                     {filteredEfficiencyData.map((entry, index) => {
                       const color = entry.outcome > 80 ? 'hsl(var(--chart-1))' : entry.outcome > 65 ? 'hsl(var(--chart-3))' : 'hsl(var(--chart-4))';
                       return <Cell key={`cell-${index}`} fill={color} />;
                      })}
                  </Scatter>
                   <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} iconSize={8} />
                </ScatterChart>
              </ResponsiveContainer>
               <p className="text-xs text-muted-foreground text-center mt-2 italic">
                    Showing resources related to: <span className="font-semibold text-primary">{selectedGoal === 'all' ? 'All Goals' : userGoals.find(g => g.id === selectedGoal)?.name}</span>.
               </p>
            </CardContent>
          </Card>

          <Card className="bg-card shadow-md border-border rounded-lg hover:shadow-lg transition-shadow duration-200">
             <CardHeader>
               <CardTitle className="text-xl font-semibold text-primary flex items-center gap-2">
                 <FileDown className="h-5 w-5 text-accent" /> Export Data
               </CardTitle>
               <CardDescription>Download your analytics snapshot.</CardDescription>
             </CardHeader>
             <CardContent className="flex flex-col items-center justify-center h-full space-y-4 pt-6">
                <p className="text-sm text-center text-muted-foreground">Generate a report of your current analytics.</p>
                 <div className="flex gap-2">
                   <Button variant="outline" size="sm"><FileDown className="h-4 w-4 mr-1" /> PDF Report</Button>
                   <Button variant="outline" size="sm"><FileDown className="h-4 w-4 mr-1" /> CSV Data</Button>
                 </div>
                 <div className="mt-auto pt-4 w-full text-center">
                    <Link to="/integrations" className="text-xs text-accent hover:underline">
                         Manage Integrations <LinkIcon className="inline h-3 w-3 ml-0.5"/>
                    </Link>
                 </div>
             </CardContent>
           </Card>
         </div>
    </div>
  );
}
